import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, MessageCircle, Phone, ShieldCheck } from 'lucide-react';

// Low-friction, Care Seeker-first intent bridge shown before the generic
// multi-role login. It preserves the visitor's intent (chat / voice) so that
// after login/sign-up they land directly in the right flow.
export default function CareSeekerBridge() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get('intent') === 'voice' ? 'voice' : 'chat';

  useEffect(() => {
    // Persisted so the seeker dashboard can resume the intent post-login.
    sessionStorage.setItem('veyonika_intent', intent);
  }, [intent]);

  const continueAsSeeker = () => {
    sessionStorage.setItem('veyonika_intent', intent);
    // Private chat now uses the low-friction guest flow (no /auth first).
    // Voice remains an authenticated flow per product decision.
    if (intent === 'chat') {
      navigate('/guest-chat');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-veyonika-bg flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Heart className="text-veyonika-maroon" size={26} />
          <span className="text-2xl font-heading font-semibold text-veyonika-maroon">Veyonika</span>
        </div>

        <div className="veyonika-card p-6 sm:p-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-veyonika-rose mb-4 mx-auto">
            {intent === 'voice'
              ? <Phone className="text-veyonika-maroonHover" size={22} />
              : <MessageCircle className="text-veyonika-maroonHover" size={22} />}
          </div>

          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-center text-veyonika-dark mb-3">
            Start your private support session
          </h1>
          <p className="text-center text-[#7A625C] leading-relaxed mb-6">
            You can begin with a free introductory {intent === 'voice' ? 'call' : 'chat'}. Your details
            stay private, and you can choose how much you share.
          </p>

          <button
            onClick={continueAsSeeker}
            data-testid="bridge-continue-seeker"
            className="btn-pill w-full mb-3"
          >
            Continue as Care Seeker
          </button>

          <button
            onClick={continueAsSeeker}
            data-testid="bridge-login"
            className="w-full text-center py-2 text-veyonika-maroon hover:text-veyonika-maroonHover font-medium transition-colors"
          >
            Already have an account? Log in
          </button>

          <div className="mt-5 pt-4 border-t border-veyonika-rose text-center">
            <button
              onClick={() => navigate('/auth?tab=provider')}
              data-testid="bridge-provider-admin-login"
              className="text-sm text-[#7A625C] hover:text-veyonika-maroon transition-colors"
            >
              Care Provider / Admin login
            </button>
          </div>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-xs text-[#7A625C] mt-5">
          <ShieldCheck size={14} className="text-veyonika-maroonHover" />
          Confidential &amp; private. You are not alone.
        </p>
      </div>
    </div>
  );
}
